import { DEFAULT_APP_SETTING } from '@ipfabric/utils';
import Immutable from 'immutable';
import { FETCH_ADVANCED } from 'pages/Settings//DiscoverySnapshots/GlobalDiscoveryConfiguration/constants';
import { fixNullCronExpression } from 'pages/Settings//DiscoverySnapshots/GlobalDiscoveryConfiguration/reducer';
import { SAVE_MAINTENANCE } from './constants';
import { isFtp } from './helpers';
import type { CronMapValue, CronValueKey } from './types';

const DEFAULT_TIMEZONE = 'UTC';
const DEFAULT_BACKUP_CRON_EXPRESSION = '0 23 * * *';
const DEFAULT_MAINTENANCE_CRON_EXPRESSION = '0 0 * * *';

export type BackupState = {
  enabled: boolean;
  cron: CronMapValue;
  type: 'local' | 'ftp' | 'sftp';
  server: string | null;
  username: string | null;
  password: string | null;
  directory: string | null;
  // Password field is optional then, with placeholder suggesting
  // that leaving it empty will preserve stored password.
  hasStoredFTPPassword: boolean;
};

export type MaintenanceState = {
  enabled: boolean;
  cron: CronMapValue;
};

export type BackupMaintenanceState = {
  backup: Immutable.Record<BackupState>;
  maintenance: Immutable.Record<MaintenanceState>;
  isLoaded: boolean;
};

export type BackupMaintenanceAction = {
  type: string;
  payload: any;
};

const Backup = Immutable.Record<BackupState>({
  enabled: false,
  cron: Immutable.Map<CronValueKey, string>({
    expression: DEFAULT_BACKUP_CRON_EXPRESSION,
    timezone: DEFAULT_TIMEZONE,
  }),
  type: 'local',
  server: null,
  username: null,
  password: null,
  directory: null,
  // Password field is optional then, with placeholder suggesting
  // that leaving it empty will preserve stored password.
  hasStoredFTPPassword: false,
});

const Maintenance = Immutable.Record<MaintenanceState>({
  cron: Immutable.Map<CronValueKey, string>({
    expression: DEFAULT_MAINTENANCE_CRON_EXPRESSION,
    timezone: DEFAULT_TIMEZONE,
  }),
  enabled: true,
});

const BackupMaintenance = Immutable.Record<BackupMaintenanceState>({
  backup: new Backup(),
  maintenance: new Maintenance(),
  isLoaded: false,
});

const initialState = new BackupMaintenance();

function backupMaintenanceReducer(
  previousState: typeof initialState,
  action: BackupMaintenanceAction,
) {
  const state = previousState ?? initialState;
  switch (action.type) {
    case `${FETCH_ADVANCED}_SUCCESS`: {
      const { backup, maintenance } = action.payload;
      return state
        .set(
          'backup',
          backup
            ? new Backup(backup)
                .update(
                  'cron',
                  fixNullCronExpression(
                    DEFAULT_APP_SETTING.backup.cron.expression,
                    true,
                  ),
                )
                .set('hasStoredFTPPassword', isFtp(backup.type))
            : new Backup(),
        )
        .set(
          'maintenance',
          maintenance
            ? new Maintenance(maintenance).update(
                'cron',
                fixNullCronExpression(
                  DEFAULT_MAINTENANCE_CRON_EXPRESSION,
                  true,
                ),
              )
            : new Maintenance(),
        )
        .set('isLoaded', true);
    }
    case `${SAVE_MAINTENANCE}_SUCCESS`: {
      return state.set(
        'maintenance',
        new Maintenance({
          enabled: action.payload.values.get('enabled'),
          cron: Immutable.Map(action.payload.values.get('cron')),
        }),
      );
    }
    default:
      return state;
  }
}

export default backupMaintenanceReducer;
